import { LuxIconType } from '@gemini/fe-ds-core';
import { ChooseNewSlotType, Patient } from '@store/patientStore';
import { StepsStep } from '@utils/enums/StepsStep.enum';

type Translate = (
  key: string,
  options?: Record<string, string | undefined>,
) => string;

export type BookingToastId =
  | 'appointment-change-disclaimer'
  | 'insurance-change-disclaimer'
  | 'choose-new-slot-appointment'
  | 'choose-new-slot-insurance'
  | 'choose-new-slot-both'
  | 'multipatient-schedule-check'
  | 'reschedule-submission-warning';

export interface BookingToastDescriptor {
  id: BookingToastId;
  key: string;
  title: string;
  description?: string;
  severity?: 'warning' | 'error' | 'success' | 'info';
  icon?: LuxIconType;
  showCloseIcon?: boolean;
  priority: number;
  onDismiss?: () => void;
}

export interface BuildBookingToastsInput {
  currentStep: StepsStep;
  changeToastType: string;
  onDismissChangeToast: () => void;
  patientsToCheck: string[];
  onDismissPatientsToCheck: () => void;
  currentPatient?: Patient;
  currentPatientIndex: number;
  patients: Array<Patient | undefined>;
  isRescheduling?: boolean;
  t: Translate;
}

const getPatientFullName = (patient?: Patient) => {
  const firstName = patient?.patientDetails?.firstName;
  const lastName = patient?.patientDetails?.lastName;

  return [firstName, lastName].filter(Boolean).join(' ');
};

const getReschedulingPatientNames = (
  patients: Array<Patient | undefined>,
  predicate: (patient: Patient) => boolean,
) =>
  patients
    .map(patient => {
      if (patient && predicate(patient)) {
        return getPatientFullName(patient);
      }
      return undefined;
    })
    .filter((patientName): patientName is string => !!patientName);

const formatPatientNames = (patientNames: string[]) => patientNames.join(', ');

const buildChangeDisclaimerToast = ({
  changeToastType,
  currentStep,
  onDismissChangeToast,
  t,
}: Pick<
  BuildBookingToastsInput,
  'changeToastType' | 'currentStep' | 'onDismissChangeToast' | 't'
>): BookingToastDescriptor | undefined => {
  if (!changeToastType || currentStep === StepsStep.SCHEDULE_EXAM) {
    return undefined;
  }

  const isAppointmentChange = changeToastType === 'appointment';

  return {
    id: isAppointmentChange
      ? 'appointment-change-disclaimer'
      : 'insurance-change-disclaimer',
    key: `change-disclaimer-${changeToastType}`,
    title: isAppointmentChange
      ? t('appointmentTypePage.changingAppErrorFooter')
      : t('fieldsLabel.changingInsuranceErrorFooter'),
    severity: 'warning',
    icon: 'WarningIconCircle',
    priority: 40,
    onDismiss: onDismissChangeToast,
  };
};

const buildChooseNewSlotToast = ({
  currentPatient,
  currentPatientIndex,
  currentStep,
  patients,
  t,
}: Pick<
  BuildBookingToastsInput,
  'currentPatient' | 'currentPatientIndex' | 'currentStep' | 'patients' | 't'
>): BookingToastDescriptor | undefined => {
  const chooseNewSlot = currentPatient?.extraInfoScheduleExam?.chooseNewSlot;

  if (
    currentStep !== StepsStep.SCHEDULE_EXAM ||
    chooseNewSlot?.type === undefined
  ) {
    return undefined;
  }

  const chooseNewSlotType = chooseNewSlot.type;
  const isInsuranceChange = chooseNewSlotType === ChooseNewSlotType.INSURANCE;
  const isBothChange = chooseNewSlotType === ChooseNewSlotType.BOTH;
  const appointmentName = patients[currentPatientIndex]?.appointmentType?.name;
  let toastId: BookingToastId = 'choose-new-slot-appointment';

  if (isInsuranceChange) {
    toastId = 'choose-new-slot-insurance';
  } else if (isBothChange) {
    toastId = 'choose-new-slot-both';
  }

  return {
    id: toastId,
    key: [
      'choose-new-slot',
      chooseNewSlotType,
      chooseNewSlot.ruleTime,
      chooseNewSlot.ruleUnit,
      appointmentName,
    ].join('-'),
    title: isInsuranceChange
      ? t('calendarPage.chooseNewSlot.insuranceChangedTitle')
      : t('calendarPage.chooseNewSlot.appointmentChangedTitle'),
    description: isInsuranceChange
      ? t('calendarPage.chooseNewSlot.insuranceChangedDescription', {
          ruleTime: chooseNewSlot.ruleTime,
          ruleUnit: chooseNewSlot.ruleUnit?.toLowerCase(),
        })
      : t('calendarPage.chooseNewSlot.appointmentChangedDescription', {
          appType: appointmentName,
        }),
    severity: 'warning',
    priority: 10,
  };
};

const buildReschedulingToast = ({
  currentPatient,
  currentStep,
  isRescheduling,
  patients,
  t,
}: Pick<
  BuildBookingToastsInput,
  'currentPatient' | 'currentStep' | 'isRescheduling' | 'patients' | 't'
>): BookingToastDescriptor | undefined => {
  if (!isRescheduling || currentStep !== StepsStep.SCHEDULE_EXAM) {
    return undefined;
  }

  const currentPatientName = getPatientFullName(currentPatient);
  const failedPatients = getReschedulingPatientNames(
    patients,
    patient => !patient.appointmentId,
  );
  const validPatients = getReschedulingPatientNames(
    patients,
    patient => !!patient.appointmentId,
  );
  const patientsToReschedule =
    failedPatients.length > 0
      ? failedPatients
      : [currentPatientName].filter(Boolean);

  return {
    id: 'reschedule-submission-warning',
    key: `reschedule-submission-warning-${formatPatientNames(patientsToReschedule)}-${formatPatientNames(validPatients)}`,
    title: t('calendarPage.rescheduleSubmissionWarning.title', {
      patients: formatPatientNames(patientsToReschedule),
    }),
    description:
      validPatients.length > 0
        ? t('calendarPage.rescheduleSubmissionWarning.description', {
            patients: formatPatientNames(validPatients),
          })
        : undefined,
    severity: 'warning',
    priority: 20,
  };
};

const buildMultipatientScheduleCheckToast = ({
  currentStep,
  onDismissPatientsToCheck,
  patientsToCheck,
  t,
}: Pick<
  BuildBookingToastsInput,
  'currentStep' | 'onDismissPatientsToCheck' | 'patientsToCheck' | 't'
>): BookingToastDescriptor | undefined => {
  if (currentStep !== StepsStep.SCHEDULE_EXAM || patientsToCheck.length === 0) {
    return undefined;
  }

  return {
    id: 'multipatient-schedule-check',
    key: `multipatient-schedule-check-${patientsToCheck.join(',')}`,
    title: t('calendarPage.checkingMultipatientsScheduleError.primaryMessage'),
    description:
      t('calendarPage.checkingMultipatientsScheduleError.secondaryMessage') +
      patientsToCheck.join(', '),
    severity: 'warning',
    priority: 30,
    onDismiss: onDismissPatientsToCheck,
  };
};

export const buildBookingToasts = (
  input: BuildBookingToastsInput,
): BookingToastDescriptor[] =>
  [
    buildChooseNewSlotToast(input),
    buildReschedulingToast(input),
    buildMultipatientScheduleCheckToast(input),
    buildChangeDisclaimerToast(input),
  ]
    .filter((toast): toast is BookingToastDescriptor => !!toast)
    .sort((first, second) => first.priority - second.priority);
